

// src/Servicefile/adminAuthService.jsx

import axios from 'axios';
import { BASE_URL } from '../../config/api';

class AdminAuthService {
    constructor() {
        //console.log('🔵 [ADMIN_AUTH] ===== INITIALIZING =====');

        this.accessToken = localStorage.getItem('authToken') ||
            localStorage.getItem('adminAuthToken') ||
            null;

        this.refreshToken = localStorage.getItem('adminRefreshToken') ||
            localStorage.getItem('refreshToken') ||
            null;

        this.userData = null;
        this.isRefreshing = false;
        this.failedQueue = [];
        this.adminApiClient = null;

        //console.log('🔵 [ADMIN_AUTH] Initial tokens:');
        //console.log('  - accessToken:', this.accessToken ? '✅ Present' : '❌ Missing');
        //console.log('  - refreshToken:', this.refreshToken ? '✅ Present' : '❌ Missing');

        // ✅ Auto-refresh token on page load if it's expiring soon
        if (this.accessToken && this.refreshToken) {
            const timeLeft = this.getTimeLeft();
            //console.log(`🔵 [ADMIN_AUTH] Token time left: ${timeLeft} seconds`);
            if (timeLeft < 300) { // Less than 5 minutes
                //console.log('🔄 [ADMIN_AUTH] Token expiring soon, refreshing on load...');
                this.refreshTokenIfNeeded();
            }
        }

        //console.log('🔵 [ADMIN_AUTH] ===== INIT COMPLETE =====');
    }

    // ✅ Also add a periodic check
    startTokenCheck() {
        // Check every minute
        this.tokenCheckInterval = setInterval(() => {
            if (this.accessToken && this.refreshToken) {
                const timeLeft = this.getTimeLeft();
                if (timeLeft < 60) { // Less than 1 minute
                    //console.log('🔄 [ADMIN_AUTH] Token expiring soon, refreshing...');
                    this.refreshTokenIfNeeded();
                }
            }
        }, 60000); // Check every minute
    }
    

    setApiClient(client) {
        //console.log('🔵 [ADMIN_AUTH] Setting API client');
        this.adminApiClient = client;
    }

    setAuthData(loginResponse) {
        //console.log('🔵 [ADMIN_AUTH] ===== SETTING AUTH DATA =====');
        //console.log('🔵 [ADMIN_AUTH] Login response keys:', Object.keys(loginResponse));
        //console.log('🔵 [ADMIN_AUTH] Token:', loginResponse.token ? '✅ Present' : '❌ Missing');
        //console.log('🔵 [ADMIN_AUTH] RefreshToken:', loginResponse.refreshToken ? '✅ Present' : '❌ Missing');
        //console.log('🔵 [ADMIN_AUTH] User ID:', loginResponse.Objectid || loginResponse.id);

        this.accessToken = loginResponse.token;
        this.refreshToken = loginResponse.refreshToken;
        this.userData = loginResponse;

        // ✅ Store in BOTH old and new keys for compatibility
        localStorage.setItem('authToken', loginResponse.token);
        localStorage.setItem('adminAuthToken', loginResponse.token);
        localStorage.setItem('refreshToken', loginResponse.refreshToken);
        localStorage.setItem('adminRefreshToken', loginResponse.refreshToken);
        localStorage.setItem('adminUserData', JSON.stringify(loginResponse));
        localStorage.setItem('adminId', loginResponse.Objectid || loginResponse.id || '');
        localStorage.setItem('adminRole', loginResponse.role || 'ADMIN');
        localStorage.setItem('userId', (loginResponse.Objectid || loginResponse.id || '').toString());

        //console.log('✅ [ADMIN_AUTH] All data stored in localStorage');
        //console.log('🔍 [ADMIN_AUTH] Verification:');
        //console.log('  - authToken:', localStorage.getItem('authToken') ? '✅' : '❌');
        //console.log('  - adminAuthToken:', localStorage.getItem('adminAuthToken') ? '✅' : '❌');
        //console.log('  - refreshToken:', localStorage.getItem('refreshToken') ? '✅' : '❌');
        //console.log('  - adminRefreshToken:', localStorage.getItem('adminRefreshToken') ? '✅' : '❌');
        //console.log('🔵 [ADMIN_AUTH] ===== AUTH DATA SET COMPLETE =====');
    }

    getAccessToken() {
        const token = this.accessToken ||
            localStorage.getItem('authToken') ||
            localStorage.getItem('adminAuthToken');

        if (token) {
            //console.log('🔑 [ADMIN_AUTH] Getting access token (first 20 chars):', token.substring(0, 20) + '...');
        } else {
            //console.log('❌ [ADMIN_AUTH] No access token available');
        }
        return token;
    }

    getRefreshToken() {
        const token = this.refreshToken ||
            localStorage.getItem('refreshToken') ||
            localStorage.getItem('adminRefreshToken');

        if (token) {
            //console.log('🔑 [ADMIN_AUTH] Getting refresh token (first 20 chars):', token.substring(0, 20) + '...');
        } else {
            //console.log('❌ [ADMIN_AUTH] No refresh token available');
        }
        return token;
    }

    setAccessToken(newToken) {
        //console.log('🔄 [ADMIN_AUTH] Updating access token...');
        //console.log('🔄 [ADMIN_AUTH] New token (first 20 chars):', newToken.substring(0, 20) + '...');

        this.accessToken = newToken;
        localStorage.setItem('authToken', newToken);
        localStorage.setItem('adminAuthToken', newToken);

        //console.log('✅ [ADMIN_AUTH] Access token updated in localStorage');
    }

    setRefreshToken(newRefreshToken) {
        //console.log('🔄 [ADMIN_AUTH] Updating refresh token...');
        //console.log('🔄 [ADMIN_AUTH] New refresh token (first 20 chars):', newRefreshToken.substring(0, 20) + '...');

        this.refreshToken = newRefreshToken;
        localStorage.setItem('refreshToken', newRefreshToken);
        localStorage.setItem('adminRefreshToken', newRefreshToken);

        //console.log('✅ [ADMIN_AUTH] Refresh token updated in localStorage');
    }

    getTimeLeft() {
        const token = this.getAccessToken();
        if (!token) {
            //console.log('⏰ [ADMIN_AUTH] No token to check expiry');
            return 0;
        }

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const exp = payload.exp;
            const currentTime = Math.floor(Date.now() / 1000);
            const timeLeft = exp - currentTime;

            //console.log(`⏰ [ADMIN_AUTH] Token expires in: ${Math.floor(timeLeft / 60)} minutes ${timeLeft % 60} seconds`);
            return timeLeft;
        } catch (error) {
            //console.log('❌ [ADMIN_AUTH] Failed to decode token:', error);
            return 0;
        }
    }

    isTokenExpired() {
        const timeLeft = this.getTimeLeft();
        const isExpired = timeLeft <= 0;
        if (isExpired) {
            //console.log('❌ [ADMIN_AUTH] Token is EXPIRED!');
        }
        return isExpired;
    }

    isTokenExpiringSoon() {
        const timeLeft = this.getTimeLeft();
        const needsRefresh = timeLeft < 300; // 5 minutes
        if (needsRefresh && timeLeft > 0) {
            //console.log(`⚠️ [ADMIN_AUTH] Token expiring soon! ${timeLeft} seconds left`);
        } else if (needsRefresh && timeLeft <= 0) {
            //console.log(`❌ [ADMIN_AUTH] Token is ALREADY EXPIRED!`);
        }
        return needsRefresh;
    }

    async refreshTokenIfNeeded() {
        //console.log('🔍 [ADMIN_AUTH] refreshTokenIfNeeded() called!');

        if (this.isRefreshing) {
            //console.log('⏳ [ADMIN_AUTH] Refresh already in progress, waiting...');
            return;
        }

        const timeLeft = this.getTimeLeft();
        //console.log(`🔍 [ADMIN_AUTH] Time left: ${timeLeft} seconds`);

        // ✅ If token has more than 5 minutes, no refresh needed
        if (timeLeft > 300) {
            //console.log('✅ [ADMIN_AUTH] Token still valid, no refresh needed');
            return;
        }

        //console.log('🔄 [ADMIN_AUTH] Token needs refresh! Starting refresh process...');
        this.isRefreshing = true;

        try {
            const refreshToken = this.getRefreshToken();
            if (!refreshToken) {
                //console.log('❌ [ADMIN_AUTH] No refresh token available');
                this.isRefreshing = false;
                return;
            }

            //console.log(`🔄 [ADMIN_AUTH] Calling refresh endpoint with refreshToken: ${refreshToken.substring(0, 20)}...`);
            //console.log(`🔄 [ADMIN_AUTH] URL: ${BASE_URL}/auth/refresh`);

            const response = await axios({
                method: 'POST',
                url: `${BASE_URL}/auth/refresh`,
                params: { refreshTokenmobile: refreshToken },
                headers: {
                    'Accept': '*/*',
                    'Content-Type': 'application/json',
                },
                withCredentials: true,
            });

            //console.log(`✅ [ADMIN_AUTH] Refresh response status: ${response.status}`);
            //console.log('✅ [ADMIN_AUTH] Refresh response data:', response.data);

            const newToken = response.data.token || response.data.accessToken;
            if (newToken) {
                //console.log('✅ [ADMIN_AUTH] New token received!');
                this.setAccessToken(newToken);

                if (response.data.refreshToken) {
                    //console.log('🔄 [ADMIN_AUTH] New refresh token received (token rotation)');
                    this.setRefreshToken(response.data.refreshToken);
                }

                //console.log('✅ [ADMIN_AUTH] ===== REFRESH COMPLETE =====');
            } else {
                //console.log('❌ [ADMIN_AUTH] No token in refresh response!');
                throw new Error('No token in refresh response');
            }
        } catch (error) {
            console.error('❌ [ADMIN_AUTH] ===== REFRESH FAILED =====');
            console.error('❌ [ADMIN_AUTH] Error message:', error.message);
            console.error('❌ [ADMIN_AUTH] Error response:', error.response?.data);
            console.error('❌ [ADMIN_AUTH] Error status:', error.response?.status);
            this.clearAuth();
        } finally {
            this.isRefreshing = false;
        }
    }

    clearAuth() {
        //console.log('🗑️ [ADMIN_AUTH] Clearing all auth data...');

        this.accessToken = null;
        this.refreshToken = null;
        this.userData = null;

        localStorage.removeItem('authToken');
        localStorage.removeItem('adminAuthToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('adminRefreshToken');
        localStorage.removeItem('adminUserData');
        localStorage.removeItem('adminId');
        localStorage.removeItem('adminRole');
        localStorage.removeItem('userId');
        localStorage.removeItem('role');
        localStorage.removeItem('vendorId');
        localStorage.removeItem('businessModules');
        localStorage.removeItem('employeeId');
        localStorage.removeItem('parentId');
        localStorage.removeItem('employeeRole');
        localStorage.removeItem('planType');

        //console.log('✅ [ADMIN_AUTH] All auth data cleared');
    }

    isLoggedIn() {
        const hasToken = !!this.getAccessToken();
        const isExpired = this.isTokenExpired();
        const isLoggedIn = hasToken && !isExpired;

        //console.log(`🔍 [ADMIN_AUTH] Login status: ${isLoggedIn ? '✅ Logged in' : '❌ Not logged in'}`);
        //console.log(`  - Has token: ${hasToken}`);
        //console.log(`  - Is expired: ${isExpired}`);

        return isLoggedIn;
    }
}

export default new AdminAuthService();